/**
 * Desktop hover zoom: scales the image under the cursor.
 * The image is position:absolute inside an overflow clip (see gallery CSS) so flex
 * layout does not fight transform; transform-origin uses % of the image border box.
 */
const ZOOM_SCALE = 3;
const TRANSITION = 'transform 0.1s ease-out';

/**
 * transform-origin as percentages of the image element (0–100), for zoom-under-pointer.
 * @param {HTMLImageElement} img
 * @param {number} clientX
 * @param {number} clientY
 * @returns {{ xPct: number; yPct: number }}
 */
function getTransformOriginPercent(img, clientX, clientY) {
  const r = img.getBoundingClientRect();
  const w = Math.max(r.width, 1);
  const h = Math.max(r.height, 1);
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  const relX = clientX - r.left;
  const relY = clientY - r.top;

  let xPx = Math.min(Math.max(relX, 0), w);
  let yPx = Math.min(Math.max(relY, 0), h);

  const objectFit = getComputedStyle(img).objectFit || 'fill';
  if (iw && ih && objectFit === 'contain') {
    const s = Math.min(w / iw, h / ih);
    const dw = iw * s;
    const dh = ih * s;
    const ox = (w - dw) / 2;
    const oy = (h - dh) / 2;
    xPx = Math.min(Math.max(relX, ox), ox + dw);
    yPx = Math.min(Math.max(relY, oy), oy + dh);
  }

  return {
    xPct: (xPx / w) * 100,
    yPct: (yPx / h) * 100,
  };
}

class ProductImageHoverZoom extends HTMLElement {
  /** @type {HTMLImageElement | null} */
  #img = null;

  /** @type {MediaQueryList | null} */
  #mq = null;

  /** @type {(() => void) | null} */
  #mqListener = null;

  /** @type {boolean} */
  #armed = false;

  connectedCallback() {
    this.#img = this.querySelector('img.product-media__image');
    if (!this.#img) return;

    const productMedia = this.#img.closest('.product-media');
    if (!(productMedia instanceof HTMLElement)) return;

    /** @type {HTMLElement} */
    let inner;
    const found = productMedia.querySelector(':scope > .product-image-hover-zoom__inner');
    if (found instanceof HTMLElement) {
      inner = found;
    } else {
      const created = document.createElement('div');
      created.className = 'product-image-hover-zoom__inner';
      productMedia.insertBefore(created, this.#img);
      created.appendChild(this.#img);
      inner = created;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#mq = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
    this.#mqListener = () => this.#syncMq();
    this.#mq.addEventListener('change', this.#mqListener);
    this.#syncMq();
  }

  disconnectedCallback() {
    if (this.#mq && this.#mqListener) {
      this.#mq.removeEventListener('change', this.#mqListener);
    }
    this.#teardown();
  }

  #syncMq() {
    this.removeEventListener('pointerenter', this.#onEnter);
    if (this.#mq?.matches) {
      this.addEventListener('pointerenter', this.#onEnter);
    } else {
      this.#teardown();
      this.#resetZoom();
    }
  }

  #onEnter = (/** @type {PointerEvent} */ e) => {
    if (e.pointerType !== 'mouse') return;
    const gallery = this.closest('media-gallery');
    const idx = this.dataset.mediaIndex;
    if (gallery && typeof idx === 'string' && 'preloadImage' in gallery) {
      /** @type {{ preloadImage: (i: number) => void }} */ (gallery).preloadImage(parseInt(idx, 10));
    }

    this.addEventListener('pointermove', this.#onMove);
    this.addEventListener('pointerleave', this.#onLeave);
    this.#armed = true;
    this.dataset.hoverZoomActive = 'true';
    this.#applyZoom(e);
  };

  #onMove = (/** @type {PointerEvent} */ e) => {
    if (!this.#armed || e.pointerType !== 'mouse') return;
    this.#applyZoom(e);
  };

  #onLeave = () => {
    this.#teardown();
    this.#resetZoom();
  };

  #teardown() {
    this.removeEventListener('pointermove', this.#onMove);
    this.removeEventListener('pointerleave', this.#onLeave);
    this.#armed = false;
    this.removeAttribute('data-hover-zoom-active');
  }

  #applyZoom(/** @type {PointerEvent} */ e) {
    const img = this.#img;
    if (!img || !(img instanceof HTMLImageElement)) return;

    const r = img.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return;

    const { xPct, yPct } = getTransformOriginPercent(img, e.clientX, e.clientY);
    img.style.transition = TRANSITION;
    img.style.transformOrigin = `${xPct}% ${yPct}%`;
    img.style.transform = `scale(${ZOOM_SCALE})`;
  }

  #resetZoom() {
    const img = this.#img;
    if (!img) return;
    img.style.transition = '';
    img.style.transformOrigin = '';
    img.style.transform = '';
  }
}

if (!customElements.get('product-image-hover-zoom')) {
  customElements.define('product-image-hover-zoom', ProductImageHoverZoom);
}
